import { inject, Injectable } from '@angular/core';
import { ToastOptions, ToastService } from './toast.service';

export interface GlobalExpectedEpError {
  code?: string;
  detail?: string;
}

export interface GlobalExpectedEpErrorResponse {
  headers: unknown;
  status: number;
  statusText: string;
  url: string;
  ok: boolean;
  name: string;
  message: string;
  error: GlobalExpectedEpError;
}

export interface HandleEpErrorConfig {
  toast?: {
    show: boolean;
    type?: 'error' | 'info' | 'warning';
    options?: ToastOptions;
  };
  defaultMessage?: string;
}

/**
 * Service to handle common errors from BE and display them
 */
@Injectable({
  providedIn: 'root',
})
export class EpErrorService {
  private readonly toastrService = inject(ToastService);

  private readonly deafultConfig: HandleEpErrorConfig = {
    toast: {
      show: true,
      type: 'error',
    },
    defaultMessage: 'An error has occured',
  };

  handleEpErrorResponse(errorResponse: GlobalExpectedEpErrorResponse, config?: HandleEpErrorConfig) {
    if (!errorResponse) {
      return;
    }

    const mergedConfig: HandleEpErrorConfig = {
      ...this.deafultConfig,
      ...config,
    };

    let messageToShow = mergedConfig.defaultMessage as string;

    if (errorResponse.error?.detail) {
      messageToShow = errorResponse.error.detail;
    } else if (errorResponse.error && typeof errorResponse.error === 'object') {
      const messages = Object.values(errorResponse.error)
        .flat()
        .filter((v): v is string => typeof v === 'string');
      if (messages.length > 0) {
        messageToShow = messages.join(' ');
      }
    }
    if (mergedConfig.toast?.show) {
      switch (mergedConfig.toast.type) {
        case 'error':
          this.toastrService.error(messageToShow, undefined, mergedConfig.toast.options);
          break;
        case 'info':
          this.toastrService.info(messageToShow, undefined);
          break;
        case 'warning':
          this.toastrService.warning(messageToShow, undefined, mergedConfig.toast.options);
          break;
        default:
          this.toastrService.error(messageToShow, undefined, mergedConfig.toast.options);
      }
    }
  }
}
